import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { cameraMessages } from 'src/features/camera/utils/messages';
import { MonitoringGateway as MonitoringGatewayType } from 'src/features/monitoring/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { translate } from 'src/features/monitoring/components/SingleMonitoringGateway';

type CameraModalProps = {
    gateway: MonitoringGatewayType;
    children?: React.ReactNode;
    className?: string;
    isModalOpen: boolean;
    isPlaying: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const CameraModal = ({ gateway, children, isModalOpen, isPlaying, setIsModalOpen }: CameraModalProps) => {
    const { formatMessage, locale } = useIntl();

    const { translation } = React.useMemo(() => {
        const translation = translate(gateway.translations, locale);

        return {
            translation,
        };
    }, [gateway, locale]);

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {isPlaying && (
                <FontAwesomeIcon
                    icon={ICON_MAP.EXPAND}
                    aria-hidden="true"
                    focusable="false"
                    className={cn('absolute right-4 bottom-8 scale-150 cursor-pointer text-white')}
                    style={{
                        filter: 'drop-shadow(0 0 1px gray) drop-shadow(0 0 1px gray)',
                    }}
                    onClick={() => setIsModalOpen(true)}
                />
            )}
            <DialogContent className="max-w-5xl">
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={ICON_MAP.VIDEO} />
                    <div>
                        <DialogTitle>{translation?.name}</DialogTitle>
                        <DialogDescription>
                            {formatMessage(cameraMessages.cameraStreamForGateway, {
                                gateway: translation?.name,
                            })}
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>{children}</DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            variant="outline"
                            type="button"
                            className="flex-1"
                            onClick={() => setIsModalOpen(false)}
                        >
                            <FormattedMessage {...generalMessages.close} />
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CameraModal;
